"use client";

import { c as _c } from "react/compiler-runtime";
import { PlaceAutocomplete } from "@/components/others/place-autocomplete";
import { Map, Marker } from "@vis.gl/react-google-maps";
import { memo, useCallback, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { MapMarkerClusterer } from "../search/map/map-marker-clusterer";
import { MapControlsFAB } from "../search/map/map-marker-fab";
import { ButtonActions } from "./action-buttons";
import { AddressFormFields } from "./address-form-fields";
import { LocationDetails } from "./address-location-details";
import { useFormContext } from "./context/FormProvider";
import { useAddressMap, type AddressFormData } from "./hooks/useAddressMap";
interface StepAddressProps {
  onNext: (data: AddressFormData) => void;
  onBack?: () => void;
  isLoading?: boolean;
}

// Memoized Map Controls component
const MemoizedMapControlsFAB = memo(MapControlsFAB);

// Memoized Loading Overlay
const LoadingOverlay = memo(t0 => {
  const $ = _c(1);
  const {
    isLoading
  } = t0;
  if (!isLoading) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20"><div className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-md border border-white/20 shadow-lg rounded-full"><svg className="animate-spin h-4 w-4 text-primary-600" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg><span className="text-sm font-medium text-gray-700">Fetching location...</span></div></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  return t1;
});
LoadingOverlay.displayName = "LoadingOverlay";

// Memoized Current Location Button
const CurrentLocationButton = memo(t0 => {
  const $ = _c(8);
  const {
    onClick,
    isLoading
  } = t0;
  const t1 = `flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 text-primary-600 rounded-lg text-sm font-medium hover:bg-gray-50 shadow-sm disabled:opacity-50 transition-colors shrink-0 ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`;
  let t2;
  if ($[0] !== isLoading) {
    t2 = isLoading ? <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="9" r="3" stroke="currentColor" strokeWidth="2" /></svg>;
    $[0] = isLoading;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span>Current Location</span>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isLoading || $[4] !== onClick || $[5] !== t1 || $[6] !== t2) {
    t4 = <button type="button" onClick={onClick} disabled={isLoading} className={t1} title="Use Current Location">{t2}{t3}</button>;
    $[3] = isLoading;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
});
CurrentLocationButton.displayName = "CurrentLocationButton";
export function StepAddress(t0) {
  const $ = _c(79);
  const {
    onNext,
    onBack,
    isLoading
  } = t0;
  let t1;
  if ($[0] !== onNext) {
    t1 = {
      onNext
    };
    $[0] = onNext;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    control,
    handleSubmit,
    propertyType,
    isCommunity,
    onSubmit,
    mapCenter,
    setMapCenter,
    nearbyProjects,
    searchInputValue,
    setSearchInputValue,
    isLoadingLocation,
    addressData,
    isHovered,
    setIsHovered,
    handlePlaceSelect,
    handleMapClick,
    handleCurrentLocation,
    handleProjectClick,
    isDisabled,
    handleZoom,
    mapZoom,
    setMapZoom
  } = useAddressMap(t1);
  let t2;
  if ($[2] !== control) {
    t2 = {
      control
    };
    $[2] = control;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const watchedValues = useWatch(t2) as AddressFormData;
  const {
    state
  } = useFormContext();
  state.profile?.propertyType?.type;
  const isProjectSupported = state.profile?.propertyType?.type === "apartment";
  let t3;
  if ($[4] !== searchInputValue) {
    t3 = searchInputValue && {
      value: searchInputValue
    };
    $[4] = searchInputValue;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = {
      className: "w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 shadow-sm",
      ...t3
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const searchInputProps = t4;
  let t5;
  if ($[8] !== setSearchInputValue) {
    t5 = () => {
      setSearchInputValue(null);
    };
    $[8] = setSearchInputValue;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleSearchFocus = t5;
  let t6;
  if ($[10] !== mapCenter?.lat || $[11] !== mapCenter?.lng) {
    t6 = () => ({
      lat: mapCenter?.lat,
      lng: mapCenter?.lng
    });
    $[10] = mapCenter?.lat;
    $[11] = mapCenter?.lng;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  mapCenter?.lat;
  mapCenter?.lng;
  let t7;
  if ($[13] !== t6) {
    t7 = t6();
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const memoizedMapCenter = t7;
  const hasProjectInfo = isProjectSupported ? Boolean(watchedValues.projectName) : true;
  const hasLocationInfo = Boolean(watchedValues.location.locality) && Boolean(watchedValues.location.pin);
  const canProceed = hasProjectInfo && hasLocationInfo;
  let t8;
  if ($[15] !== handleSearchFocus || $[16] !== searchInputProps) {
    t8 = {
      ...searchInputProps,
      onFocus: handleSearchFocus
    };
    $[15] = handleSearchFocus;
    $[16] = searchInputProps;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== handlePlaceSelect || $[19] !== t8) {
    t9 = <div className="flex-1 shadow-sm rounded-lg"><PlaceAutocomplete isScrollIntoView={false} onPlaceSelect={handlePlaceSelect} placeholder="Search for project name or location..." inputProps={t8} /></div>;
    $[18] = handlePlaceSelect;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== handleCurrentLocation || $[22] !== isLoadingLocation) {
    t10 = <CurrentLocationButton onClick={handleCurrentLocation} isLoading={isLoadingLocation} />;
    $[21] = handleCurrentLocation;
    $[22] = isLoadingLocation;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <div className="flex gap-4 items-center w-full">{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      height: "42vh"
    };
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      width: "100%",
      height: "100%"
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== nearbyProjects) {
    t14 = nearbyProjects || [];
    $[29] = nearbyProjects;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== handleProjectClick || $[32] !== isHovered || $[33] !== mapZoom || $[34] !== setIsHovered || $[35] !== setMapCenter || $[36] !== setMapZoom || $[37] !== t14) {
    t15 = <MapMarkerClusterer projects={t14} isHovered={isHovered} setIsHovered={setIsHovered} mapZoom={mapZoom} handleProjectClick={handleProjectClick} setMapCenter={setMapCenter} setMapZoom={setMapZoom} />;
    $[31] = handleProjectClick;
    $[32] = isHovered;
    $[33] = mapZoom;
    $[34] = setIsHovered;
    $[35] = setMapCenter;
    $[36] = setMapZoom;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== mapCenter) {
    t16 = mapCenter && <Marker key="selectedLocation" position={mapCenter} title="Selected Location" draggable={true} />;
    $[39] = mapCenter;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== handleMapClick || $[42] !== handleZoom || $[43] !== mapZoom || $[44] !== memoizedMapCenter || $[45] !== t15 || $[46] !== t16) {
    t17 = <Map defaultCenter={memoizedMapCenter} defaultZoom={14} onClick={handleMapClick} gestureHandling="greedy" style={t13} mapId="address-picker-map" zoom={mapZoom} reuseMaps={true} onZoomChanged={handleZoom} disableDefaultUI={true}>{t15}{t16}</Map>;
    $[41] = handleMapClick;
    $[42] = handleZoom;
    $[43] = mapZoom;
    $[44] = memoizedMapCenter;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <MemoizedMapControlsFAB />;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== isLoadingLocation) {
    t19 = <LoadingOverlay isLoading={isLoadingLocation} />;
    $[49] = isLoadingLocation;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t17 || $[52] !== t19) {
    t20 = <div className="relative w-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={t12}>{t17}{t18}{t19}</div>;
    $[51] = t17;
    $[52] = t19;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== handleSubmit || $[55] !== onSubmit) {
    t21 = handleSubmit(onSubmit);
    $[54] = handleSubmit;
    $[55] = onSubmit;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== addressData || $[58] !== mapCenter) {
    t22 = <LocationDetails addressData={addressData} mapCenter={mapCenter} />;
    $[57] = addressData;
    $[58] = mapCenter;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== control || $[61] !== isCommunity || $[62] !== isDisabled || $[63] !== isProjectSupported || $[64] !== propertyType) {
    t23 = <AddressFormFields control={control} propertyType={propertyType} isCommunity={isCommunity} isDisabled={isDisabled} isProjectSupported={isProjectSupported} />;
    $[60] = control;
    $[61] = isCommunity;
    $[62] = isDisabled;
    $[63] = isProjectSupported;
    $[64] = propertyType;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  const t24 = !canProceed;
  let t25;
  if ($[66] !== isLoading || $[67] !== onBack || $[68] !== t24) {
    t25 = <ButtonActions next={true} onBack={onBack} disabled={t24} isLoading={isLoading} />;
    $[66] = isLoading;
    $[67] = onBack;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== t21 || $[71] !== t22 || $[72] !== t23 || $[73] !== t25) {
    t26 = <form onSubmit={t21} className="space-y-6">{t22}{t23}{t25}</form>;
    $[70] = t21;
    $[71] = t22;
    $[72] = t23;
    $[73] = t25;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== t11 || $[76] !== t20 || $[77] !== t26) {
    t27 = <div className="h-full flex flex-col space-y-6">{t11}{t20}{t26}</div>;
    $[75] = t11;
    $[76] = t20;
    $[77] = t26;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  return t27;
}